import type {CheckPropertyUsagesResponse, PropertyDefinition} from '@github-ui/custom-properties-types'
import {checkPropertyUsagePath, propertyDefinitionSettingsPath} from '@github-ui/paths'
import sudo from '@github-ui/sudo'
import {useNavigate} from '@github-ui/use-navigate'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {Box, Button, Dialog, Text} from '@primer/react'
import {useEffect, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {useSetFlash} from '../contexts/FlashContext'
import {DefinitionUsageBanner, ServerErrorFormBanner} from './Banners'

interface Props {
  definition: PropertyDefinition
  onCancel(): void
  onDismiss(): void
}
export function DeleteDefinitionDialog({definition, onDismiss, onCancel}: Props) {
  const setFlash = useSetFlash()
  const org = useCurrentOrg()
  const navigate = useNavigate()
  const [deleting, setDeleting] = useState(false)
  const [serverErrorMessage, setServerErrorMessage] = useState('')
  const [usages, setUsages] = useState<CheckPropertyUsagesResponse | undefined>()

  const allowDeletion = usages?.propertyConsumerUsages.length === 0

  async function deleteDefinition() {
    setDeleting(true)
    setServerErrorMessage('')

    if (!(await sudo())) {
      setServerErrorMessage('Unauthorized')
      setDeleting(false)
      return
    }

    const result = await verifiedFetchJSON(
      propertyDefinitionSettingsPath({org: org.login, propertyName: definition.propertyName}),
      {method: 'DELETE'},
    )
    if (result.ok) {
      setFlash('definition.deleted.success')
      navigate(propertyDefinitionSettingsPath({org: org.login}))
    } else {
      setServerErrorMessage('Something went wrong')
      setDeleting(false)
    }
  }

  useEffect(() => {
    async function checkUsages() {
      const result = await verifiedFetchJSON(
        checkPropertyUsagePath({org: org.login, propertyName: definition.propertyName}),
      )
      if (result.ok) {
        setUsages(await result.json())
      }
    }

    checkUsages()
  }, [definition.propertyName, org.login])

  return (
    <Dialog
      data-testid="delete-definition-dialog"
      isOpen={true}
      onDismiss={onDismiss}
      aria-labelledby="delete-definition-dialog"
    >
      <Dialog.Header id="delete-definition-dialog">Delete property</Dialog.Header>
      {serverErrorMessage && (
        <Box sx={{mx: 3, mt: 2}}>
          <ServerErrorFormBanner>{serverErrorMessage}</ServerErrorFormBanner>
        </Box>
      )}

      <Box>
        <Box sx={{p: 3}}>
          {usages === undefined ? (
            <Text>Checking usages...</Text>
          ) : (
            <DefinitionUsageBanner
              name={definition.propertyName}
              repoCount={usages.repositoriesCount}
              usages={usages.propertyConsumerUsages}
            />
          )}
        </Box>
        <Box sx={{px: 3, pb: 3}}>
          <Text>
            This will permanently delete this property. <strong>This cannot be undone.</strong>
          </Text>
        </Box>

        <Box as="hr" sx={{m: 0}} />

        <Box sx={{p: 3, display: 'flex', justifyContent: 'flex-end', gap: 2}}>
          <Button onClick={onCancel}>Cancel</Button>

          {allowDeletion && (
            <Button variant="danger" onClick={deleteDefinition} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  )
}

try{ DeleteDefinitionDialog.displayName ||= 'DeleteDefinitionDialog' } catch {}